import React from 'react'

import { useHistory } from 'react-router-dom'

import {
  ColumnFilter,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  UserPermission,
  useNotify
} from '@globaleris/ui.library'
import { Delete, Pageview } from '@material-ui/icons'
import { useTranslation } from 'react-i18next'
import { FlexibilitiesModel } from '../../../api/Argentia/endpoints/flexibilities/model/flexibilities'
import { getUserPermissions } from '../../../helpers/permissionsHelper'
import { useConfirmationDialog, useHttpError } from '../../../hooks'
import i18n from '../../../i18n'
import { NonAuthRoutes } from '../../../model'
import { Pages } from '../../../model/RBAC/constants'
import { FlexibilityTableModel } from '../../../pages/flexibility/flexibility.Model'
import {
  DataTableFilter,
  getCustomerDomain,
  storeDataTableFilterForCurrentPage,
  storeDataTableSearchForCurrentPage
} from '../../../redux'
import { DataTableErrorHandler } from '../../errors/scopes/dataTable/dataTableError'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMoneyCheckDollar } from '@fortawesome/free-solid-svg-icons'
import { deleteFlexibility } from './flexibilitiesTable.service'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'

export interface FlexibilitiesTableProps {
  flexibilities?: FlexibilitiesModel[]
  isFetchingFlexibilities: boolean
  refreshFlexibilities: () => void
  onClickViewPriceComponents: (flexibility: FlexibilityTableModel) => void
  onClickAddFlexibility?: () => void
  columnsConfig?: DataTableColumn[]
  dataTableFilter?: DataTableFilter
  dataTableSearch?: string
}

export const FlexibilitiesTable: React.FC<FlexibilitiesTableProps> = ({
  flexibilities,
  columnsConfig,
  isFetchingFlexibilities,
  refreshFlexibilities,
  onClickViewPriceComponents,
  onClickAddFlexibility
}) => {
  const [t] = useTranslation(['flexibility', 'enums'])

  const { info, error } = useNotify()
  const { showError, clearError, errorProps } = useHttpError()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const onAddFlexibilityClickHandler = () => {}

  let history = useHistory()

  const handleClickDelete = async (id: number) => {
    await getConfirmation(ConfirmationDialogs.DELETE_FLEXIBILITY).then(async confirm => {
      const response = await deleteFlexibility(id)
      if (response.success) {
        info(t('flexibility:dataTable.notifications.delete.success'))
        refreshFlexibilities()
      } else {
        error(t('flexibility:dataTable.notifications.delete.error'))
      }
    })
    closeConfirmation()
  }

  const options: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 0,
    filterType: FilterType.ADVANCED,
    download: true,
    print: false,
    selectableRows: false,
    canInsert: !!onClickAddFlexibility,
    canDelete: false,
    canEdit: false,
    analytics: {
      customer: getCustomerDomain(),
      view: t('flexibility:dataTable.analytics')
    },
    fabLabel: t('flexibility:dataTable.fab'),
    customInsertEditModal: true,
    onRowInsert: async () => {
      onClickAddFlexibility && onClickAddFlexibility()
      return await true
    },
    rowCustomActions: [
      {
        icon: <Pageview fontSize="small" />,
        tooltip: t('flexibility:dataTable.tooltips.view'),
        onClick: (row: FlexibilityTableModel) => {
          history.push(NonAuthRoutes.flexibilityEdit, { flexibilityId: row.id })
        }
      },
      {
        icon: <FontAwesomeIcon size="sm" icon={faMoneyCheckDollar as IconProp} />,
        tooltip: t('flexibility:dataTable.tooltips.viewPriceComponent'),
        onClick: (row: FlexibilityTableModel) => {
          onClickViewPriceComponents(row)
        }
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <Delete fontSize="small" />,
        tooltip: t('flexibility:dataTable.tooltips.delete'),
        onClick: (row: FlexibilityTableModel) => handleClickDelete(row.id)
      }
    ],
    onFilterChange: (filter: ColumnFilter[]) => {
      storeDataTableFilterForCurrentPage(filter)
    },
    onSearchChange: (search: string) => {
      storeDataTableSearchForCurrentPage(search)
    }
  }

  // DataTable Config Starts
  const columns = columnsConfig ?? [
    {
      name: 'id',
      display: false,
      type: DataType.NUMBER
    },
    {
      name: 'subtype',
      label: i18n.t('flexibility:dataTable.columns.subtype'),
      type: DataType.TEXT
      //columnWidth: 250
    },
    {
      name: 'description',
      label: i18n.t('flexibility:dataTable.columns.description'),
      type: DataType.TEXT
      //columnWidth: 250
    },
    {
      name: 'period',
      label: i18n.t('flexibility:dataTable.columns.period'),
      type: DataType.TEXT
      //columnWidth: 250
    },
    {
      name: 'contract',
      label: i18n.t('flexibility:dataTable.columns.contract'),
      type: DataType.TEXT
      //columnWidth: 250
    },
    {
      name: 'cups',
      label: i18n.t('flexibility:dataTable.columns.cups'),
      type: DataType.TEXT
      //columnWidth: 250
    },
    {
      name: 'subject',
      label: i18n.t('flexibility:dataTable.columns.subject'),
      type: DataType.TEXT
      // columnWidth: 250
    }
    // Se oculta la columna de precio ya que la fórmula muestra los ids de los precios en lugar de su naturalLang
    // {
    //   name: 'price',
    //   label: i18n.t('flexibility:dataTable.columns.price'),
    //   type: DataType.TEXT
    //   //columnWidth: 250
    // }
  ]

  // DataTable Config Ends

  return (
    <DataTable
      data={flexibilities}
      columns={columns}
      options={options}
      info={DataTableErrorHandler(errorProps)}
      userPermissions={getUserPermissions(Pages.Flexibility)}
      // appliedFilter={dataTableFilter}
      // appliedSearch={dataTableSearch}
    />
  )
}
